import type { Caption } from '@remotion/captions';

// 인접한 caption 간 시간 gap이 이 값을 초과하면 별도 페이지로 강제 분리.
// AI 파이프라인이 시간적으로 떨어진 단어들을 하나의 caption asset으로 묶어 저장한
// 기존 데이터를 FE 렌더 단계에서 방어적으로 분리하기 위함.
//
// AI 측 1차 임계치(base_video_create_service.py의 MAX_INTRA_WINDOW_GAP_MS=3000)와
// 연동된 2단계 가드다. AI가 3초 초과 gap window를 reject해 신규 오염을 차단하면,
// 여기서는 1.5초 임계치로 AI가 허용했거나 기존 저장된 데이터까지 페이지 단위로 분리.
export const CAPTION_GAP_SPLIT_THRESHOLD_MS = 1500;

/**
 * Caption에 AlignWord로부터 전파된 speaker 라벨이 extra property로 동봉된다
 * (hydrate-editor-state-utils.ts의 buildCaptionsFromClip). @remotion/captions의
 * Caption 타입에 speaker 필드가 없어 외부에서 확장 대신 객체 extra로 유지한다.
 * WhisperX fallback 또는 화자 분리 미사용 경로에서는 undefined.
 */
type CaptionWithSpeaker = Caption & { speaker?: string[] };
type CaptionWithGroup = Caption & { captionGroupId?: string };

const getPrimarySpeaker = (c: Caption): string | undefined => {
  // Array.isArray 가드: 외부 주입/수동 편집 액션으로 speaker가 비정상 값(문자열,
  // 객체 등)으로 오염되어도 안전. speaker[]가 아니면 undefined로 fallback.
  const s = (c as CaptionWithSpeaker).speaker;
  return Array.isArray(s) && s.length > 0 ? s[0] : undefined;
};

const getCaptionGroupId = (c: Caption): string | undefined => {
  const groupId = (c as CaptionWithGroup).captionGroupId;
  return typeof groupId === 'string' && groupId.length > 0
    ? groupId
    : undefined;
};

export const splitCaptionsByGap = (
  captions: Caption[],
  gapMs: number,
): Caption[][] => {
  if (captions.length === 0) return [];
  // 상류(AI STT → BE 직렬화 → FE hydrate)는 startMs 오름차순 정렬을 보장한다.
  // 다만 외부 주입·수동 편집 액션의 회귀·향후 파이프라인 변경으로 비정렬 입력이
  // 들어오면 `curr.startMs - prev.endMs`가 음수가 되어 그룹핑이 깨진다.
  // 원본을 mutate하지 않도록 복사본을 정렬해 방어한다. O(n log n)이지만
  // 호출부(`CaptionsLayer`의 `useMemo`)에서 captions 참조 변경 시에만 재실행된다.
  const sortedCaptions = [...captions].sort((a, b) => a.startMs - b.startMs);
  const groups: Caption[][] = [];
  let currentGroup: Caption[] = [];
  for (const curr of sortedCaptions) {
    const prev = currentGroup[currentGroup.length - 1];
    if (prev) {
      const timeGapExceeded = curr.startMs - prev.endMs > gapMs;
      // 화자 분리: 양쪽 모두 speaker 라벨이 있고 서로 다르면 페이지 분리.
      // 한쪽이라도 undefined면(예: WhisperX fallback, STT 노이즈) 기존 gap 로직만 사용.
      const prevSpk = getPrimarySpeaker(prev);
      const currSpk = getPrimarySpeaker(curr);
      const speakerChanged =
        prevSpk !== undefined && currSpk !== undefined && prevSpk !== currSpk;
      const prevGroupId = getCaptionGroupId(prev);
      const currGroupId = getCaptionGroupId(curr);
      const explicitGroupChanged =
        prevGroupId !== undefined &&
        currGroupId !== undefined &&
        prevGroupId !== currGroupId;
      if (timeGapExceeded || speakerChanged || explicitGroupChanged) {
        groups.push(currentGroup);
        currentGroup = [curr];
        continue;
      }
    }
    currentGroup.push(curr);
  }
  if (currentGroup.length > 0) groups.push(currentGroup);
  return groups;
};
